import Vue from 'vue'

// Активные алерты матчей MLBB, которые приходят по WebSocket
// (mlbb-match.alert-raised / mlbb-match.alert-resolved).
//
// Состояние живёт на уровне модуля, а не внутри компонента сетки: переподключение
// сокета или перемонтирование сетки не должно гасить уже поднятые алерты, а
// повторно полученное событие не должно создавать дубликат состояния.
// Алерты внутри группы хранятся по отдельности, поэтому снятие одного алерта не
// убирает индикатор, пока по этому group_id остаются другие активные.

export const ALERT_RAISED_EVENT = 'mlbb-match.alert-raised'
export const ALERT_RESOLVED_EVENT = 'mlbb-match.alert-resolved'

// Имя события у разных алертов отличается префиксом (mlbb-match.*, mlbb-match.live.*),
// постоянен только хвост — по нему и определяем, что это алерт.
const RAISED_SUFFIX = '.alert-raised'
const RESOLVED_SUFFIX = '.alert-resolved'

export function isAlertRaisedEvent(event) {
    return String(event || '').endsWith(RAISED_SUFFIX)
}

export function isAlertResolvedEvent(event) {
    return String(event || '').endsWith(RESOLVED_SUFFIX)
}

const DEFAULT_ALERT_COLOR = '#dc3545'

// Бэкенд присылает цвет словом ('red'), приводим к палитре админки.
// Незнакомое значение отдаём как есть — вдруг это уже валидный css-цвет.
const COLORS_BY_NAME = {
    red: '#dc3545',
    orange: '#fd7e14',
    yellow: '#ffc107',
    green: '#28a745',
    blue: '#0427c4',
    grey: '#7d7d8d',
    gray: '#7d7d8d'
}

const state = Vue.observable({
    alertsByGroup: {}
})

function groupKey(groupId) {
    if (groupId === null || groupId === undefined || groupId === '') return null
    return String(groupId)
}

// Один и тот же алерт может прийти повторно (реконнект, дубль события из двух
// каналов) — ключ гарантирует, что в состоянии он останется единственным.
function alertKey(alert) {
    if (alert && alert.id != null) return `id:${alert.id}`
    return `type:${getAlertType(alert)}`
}

function isResolvedStatus(status) {
    const value = String(status || '').toLowerCase()
    return value === 'resolved' || value === 'closed'
}

// Тип алерта бэкенд присылает под разными ключами: type / types / kind.
export function getAlertType(alert) {
    if (!alert) return ''
    return String(alert.type || alert.types || alert.kind || '').toLowerCase()
}

export function describeMatchAlert(alert) {
    const type = getAlertType(alert)

    if (type.includes('rehost')) return { type, variant: 'rehost', title: 'Запрос на рехост', description: '' }
    if (type.includes('appeal')) return { type, variant: 'appeal', title: 'Подана апелляция', description: '' }
    if (type.includes('timeout')) return { type, variant: 'timeout', title: 'Таймаут', description: '' }
    if (type.includes('result')) return { type, variant: 'result', title: 'Результат матча', description: '' }
    if (type.includes('live')) {
        return {
            type,
            variant: 'live',
            title: 'Матч перешёл в статус live',
            description: 'лобби создано, игра идёт'
        }
    }

    return { type, variant: 'info', title: 'Алерт', description: '' }
}

// Строка для подсказки/списка: «Матч перешёл в статус live: лобби создано, игра идёт».
export function getMatchAlertText(alert) {
    const { title, description } = describeMatchAlert(alert)
    return description ? `${title}: ${description}` : title
}

export function getGroupAlerts(groupId) {
    const key = groupKey(groupId)
    if (!key) return []
    return state.alertsByGroup[key] || []
}

export function hasGroupAlert(groupId) {
    return getGroupAlerts(groupId).length > 0
}

// Цвет берём из последнего поднятого алерта группы.
export function getGroupAlertColor(groupId) {
    const alerts = getGroupAlerts(groupId)

    for (let i = alerts.length - 1; i >= 0; i--) {
        const color = String(alerts[i].color || '').trim()
        if (color) return COLORS_BY_NAME[color.toLowerCase()] || color
    }

    return DEFAULT_ALERT_COLOR
}

export function getGroupAlertTooltip(groupId) {
    const alerts = getGroupAlerts(groupId)
    if (!alerts.length) return ''

    const titles = [...new Set(alerts.map(alert => getMatchAlertText(alert)))]
    return `Активные алерты: ${titles.join(', ')}`
}

export function raiseMatchAlert(alert) {
    const key = groupKey(alert && alert.group_id)
    if (!key) return false

    if (isResolvedStatus(alert.status)) return resolveMatchAlert(alert)

    const currentKey = alertKey(alert)
    const next = getGroupAlerts(key).filter(item => alertKey(item) !== currentKey)
    next.push(alert)
    Vue.set(state.alertsByGroup, key, next)

    return true
}

export function resolveMatchAlert(alert) {
    const key = groupKey(alert && alert.group_id)
    if (!key) return false

    const current = state.alertsByGroup[key]
    if (!current) return false

    const currentKey = alertKey(alert)
    const next = current.filter(item => alertKey(item) !== currentKey)

    if (next.length) {
        Vue.set(state.alertsByGroup, key, next)
    } else {
        Vue.delete(state.alertsByGroup, key)
    }

    return true
}

// Синхронизация с ответом API (деталка матча отдаёт актуальный список алертов):
// переписываем состояние группы целиком, чтобы снятый через админку алерт
// пропадал с карточки, даже если событие alert-resolved не пришло.
export function setGroupAlerts(groupId, alerts) {
    const key = groupKey(groupId)
    if (!key) return false

    const next = []
    const seen = new Set()

    ;(alerts || []).forEach(alert => {
        if (!alert || isResolvedStatus(alert.status)) return
        const currentKey = alertKey(alert)
        if (seen.has(currentKey)) return
        seen.add(currentKey)
        next.push(alert)
    })

    if (next.length) {
        Vue.set(state.alertsByGroup, key, next)
    } else {
        Vue.delete(state.alertsByGroup, key)
    }

    return true
}

// Возвращает true, если сообщение было алертом и состояние обработано.
export function handleMatchAlertEvent(message) {
    const event = message && (message.event || message.type)
    const isRaised = isAlertRaisedEvent(event)
    if (!isRaised && !isAlertResolvedEvent(event)) return false

    const alert = message.data && message.data.mlbb_match_alert
    if (!alert) return true

    if (isRaised) {
        raiseMatchAlert(alert)
    } else {
        resolveMatchAlert(alert)
    }

    return true
}

export function clearMatchAlerts() {
    Object.keys(state.alertsByGroup).forEach(key => Vue.delete(state.alertsByGroup, key))
}
